import { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import {
  Sidebar,
  Overview,
  Templates,
  Contacts,
  AgentManagement,
  Settings,
  Subscribe,
  Campaign,
  Monitor,
  Chats,
  Notifications,
  FollowUps,
  ManageAssignedChats,
  ManageQuickReply,
  ManageSubscription,
  ManageAutoReplies,
  RechargeModule,
  WhatsappManager,
  ActivityLogs,
  ManageMyLinks,
  FlowChatbot,
  AllChatBots,
  QrGenerator,
  RechargeHistory,
  WaFlowsSubmissions,
  OrderDetails,
  NotificationStrip,
  BusinessCardDetails,
  AppIntegrationManager,
  // DashboardNav,
  // AddonSubscriptions,
  // Catalog,
  // ManageWelcomeMessages,
} from "../components";
import { ContactsContextProvider } from "../contexts/ContactsContextProvider";
import { useUserContext } from "../contexts/UserContextProvider";
import { useWhatsappContext } from "../contexts/WhatsappContextProvider";
import useSocket from "../components/utils/useSocket";
import { isFeatureAccessible } from "../components/utils/privacyCheck";

const Dashboard = () => {
  const { socketConnectionStatus } = useSocket();
  const location = useLocation();
  const { isLoggedIn, currentUserInfo, isUserAdminOrSuperadmin } =
    useUserContext();
  const { apiFetchWabApiCreds } = useWhatsappContext();
  const [isSocketConnected, setIsSocketConnected] = useState(false);

  useEffect(() => {
    apiFetchWabApiCreds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsSocketConnected(socketConnectionStatus);
  }, [socketConnectionStatus]);

  return (
    isLoggedIn && (
      <ContactsContextProvider>
        <div className="bg-slate-50 max-w-screen flex flex-col md:flex-row overflow-hidden">
          <Sidebar isSocketConnected={isSocketConnected} />
          <div className="w-full min-h-screen relative overflow-y-auto">
            {!currentUserInfo?.isActiveSubscription &&
              currentUserInfo?.pricingModel === 0 &&
              location.pathname !== "/dashboard/subscribe" && (
                <NotificationStrip
                  message="👀 Don't miss out! Subscribe now to continue enjoying
        enhanced experience and exclusive benefits."
                  navlink="/dashboard/subscribe"
                  navBtnText="Subscribe Now"
                />
              )}

            {currentUserInfo?.isTokenExpired === 1 && (
              <NotificationStrip
                message="Your API token is expired. Contact your admin to complete WAB Re-Signup
        to continue."
                navlink="/dashboard/settings"
                navBtnText="Go to Settings"
              />
            )}
            <div className="p-5">
              <Routes>
                <Route path="/" element={<Navigate to="overview" />} />

                <Route path="overview" element={<Overview />} />
                <Route
                  path="chats"
                  element={
                    isFeatureAccessible(currentUserInfo) ? (
                      <Chats />
                    ) : currentUserInfo?.pricingModel === 1 &&
                      currentUserInfo?.balanceCoins < 10 ? (
                      <Navigate replace to="/dashboard/recharge" />
                    ) : (
                      <Navigate replace to="/dashboard/subscribe" />
                    )
                  }
                />
                <Route path="templates" element={<Templates />} />
                <Route
                  path="contacts"
                  element={
                    isUserAdminOrSuperadmin() ? (
                      <Contacts />
                    ) : (
                      <Navigate replace to="/page-not-found" />
                    )
                  }
                />
                <Route
                  path="campaign"
                  element={
                    isFeatureAccessible(currentUserInfo) ? (
                      <Campaign />
                    ) : currentUserInfo?.pricingModel === 1 &&
                      currentUserInfo?.balanceCoins < 10 ? (
                      <Navigate replace to="/dashboard/recharge" />
                    ) : (
                      <Navigate replace to="/dashboard/subscribe" />
                    )
                  }
                />
                <Route path="monitor" element={<Monitor />} />
                <Route
                  path="agents"
                  element={
                    isFeatureAccessible(currentUserInfo) ? (
                      isUserAdminOrSuperadmin() ? (
                        <AgentManagement />
                      ) : (
                        <Navigate replace to="/page-not-found" />
                      )
                    ) : currentUserInfo?.pricingModel === 1 &&
                      currentUserInfo?.balanceCoins < 10 ? (
                      <Navigate replace to="/dashboard/recharge" />
                    ) : (
                      <Navigate replace to="/dashboard/subscribe" />
                    )
                  }
                />
                <Route
                  path="auto-replies"
                  element={
                    isFeatureAccessible(currentUserInfo) ? (
                      isUserAdminOrSuperadmin() ? (
                        <ManageAutoReplies />
                      ) : (
                        <Navigate replace to="/page-not-found" />
                      )
                    ) : currentUserInfo?.pricingModel === 1 &&
                      currentUserInfo?.balanceCoins < 10 ? (
                      <Navigate replace to="/dashboard/recharge" />
                    ) : (
                      <Navigate replace to="/dashboard/subscribe" />
                    )
                  }
                />
                <Route
                  path="follow-ups"
                  element={
                    isFeatureAccessible(currentUserInfo) ? (
                      <FollowUps />
                    ) : currentUserInfo?.pricingModel === 1 &&
                      currentUserInfo?.balanceCoins < 10 ? (
                      <Navigate replace to="/dashboard/recharge" />
                    ) : (
                      <Navigate replace to="/dashboard/subscribe" />
                    )
                  }
                />
                <Route
                  path="assigned-chats"
                  element={
                    isUserAdminOrSuperadmin() ? (
                      <ManageAssignedChats />
                    ) : (
                      <Navigate replace to="/page-not-found" />
                    )
                  }
                />
                <Route path="quick-reply" element={<ManageQuickReply />} />
                <Route
                  path="activity-logs"
                  element={
                    isUserAdminOrSuperadmin() ? (
                      <ActivityLogs />
                    ) : (
                      <Navigate replace to="/page-not-found" />
                    )
                  }
                />
                <Route path="whatsapp-manager" element={<WhatsappManager />} />

                {/* Subscription or App Level Routes */}
                <Route path="notifications" element={<Notifications />} />
                <Route path="settings" element={<Settings />} />
                <Route path="subscribe" element={<Subscribe />} />
                <Route
                  path="subscriptions"
                  element={
                    isUserAdminOrSuperadmin() ? (
                      <ManageSubscription />
                    ) : (
                      <Navigate replace to="/page-not-found" />
                    )
                  }
                />
                <Route
                  path="wa-form-submissions"
                  element={<WaFlowsSubmissions />}
                />
                <Route path="recharge" element={<RechargeModule />} />
                <Route path="recharge-history" element={<RechargeHistory />} />

                <Route
                  path="chatbots"
                  element={
                    isFeatureAccessible(currentUserInfo) ? (
                      isUserAdminOrSuperadmin() ? (
                        <AllChatBots />
                      ) : (
                        <Navigate replace to="/page-not-found" />
                      )
                    ) : currentUserInfo?.pricingModel === 1 &&
                      currentUserInfo?.balanceCoins < 10 ? (
                      <Navigate replace to="/dashboard/recharge" />
                    ) : (
                      <Navigate replace to="/dashboard/subscribe" />
                    )
                  }
                />
                <Route
                  path="flow-chatbot/:chatbotId"
                  element={
                    isFeatureAccessible(currentUserInfo) ? (
                      isUserAdminOrSuperadmin() ? (
                        <FlowChatbot />
                      ) : (
                        <Navigate replace to="/page-not-found" />
                      )
                    ) : currentUserInfo?.pricingModel === 1 &&
                      currentUserInfo?.balanceCoins < 10 ? (
                      <Navigate replace to="/dashboard/recharge" />
                    ) : (
                      <Navigate replace to="/dashboard/subscribe" />
                    )
                  }
                />
                <Route
                  path="flow-chatbot"
                  element={
                    isFeatureAccessible(currentUserInfo) ? (
                      isUserAdminOrSuperadmin() ? (
                        <FlowChatbot />
                      ) : (
                        <Navigate replace to="/page-not-found" />
                      )
                    ) : currentUserInfo?.pricingModel === 1 &&
                      currentUserInfo?.balanceCoins < 10 ? (
                      <Navigate replace to="/dashboard/recharge" />
                    ) : (
                      <Navigate replace to="/dashboard/subscribe" />
                    )
                  }
                />
                <Route
                  path="qr-generator"
                  element={
                    isUserAdminOrSuperadmin() ? (
                      <QrGenerator />
                    ) : (
                      <Navigate replace to="/page-not-found" />
                    )
                  }
                />
                <Route
                  path="mylinks"
                  element={
                    isUserAdminOrSuperadmin() ? (
                      <ManageMyLinks />
                    ) : (
                      <Navigate replace to="/page-not-found" />
                    )
                  }
                />
                <Route path="order-details" element={<OrderDetails />} />
                <Route
                  path="business-cards"
                  element={<BusinessCardDetails />}
                />

                <Route
                  path="app-integrations"
                  element={<AppIntegrationManager />}
                />

                {/* Currently Hidden Routes */}
                {/* <Route
                  path="welcome-messages"
                  element={
                    isUserAdminOrSuperadmin() ? (
                      <ManageWelcomeMessages />
                    ) : (
                      <Navigate replace to="/page-not-found" />
                    )
                  }
                /> */}

                {/* <Route
                  path="catalog"
                  element={
                    isFeatureAccessible(currentUserInfo) ? (
                      <Catalog />
                    ) : currentUserInfo?.pricingModel === 1 &&
                      currentUserInfo?.balanceCoins < 10 ? (
                      <Navigate replace to="/dashboard/recharge" />
                    ) : (
                      <Navigate replace to="/dashboard/subscribe" />
                    )
                  }
                /> */}

                {/* <Route
                  path="addons"
                  element={
                    isFeatureAccessible(currentUserInfo) ? (
                      <AddonSubscriptions />
                    ) : currentUserInfo?.pricingModel === 1 &&
                      currentUserInfo?.balanceCoins < 10 ? (
                      <Navigate replace to="/dashboard/recharge" />
                    ) : (
                      <Navigate replace to="/dashboard/subscribe" />
                    )
                  }
                /> */}
              </Routes>
            </div>
          </div>
        </div>
      </ContactsContextProvider>
    )
  );
};

export default Dashboard;
